import React from 'react';
import { Card, Row, Col, Tag, Typography, Space, Spin, Empty, Tooltip, Avatar, theme } from 'antd';
import { TrophyOutlined, CheckCircleOutlined, ClockCircleOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { BracketData, TournamentRoundDto, TournamentMatchDto, TournamentParticipant } from '@types';

const { Text, Title } = Typography;

interface TournamentBracketViewProps {
  bracket: BracketData;
  currentUserId?: string;
  isLoading?: boolean;
}

interface MatchCardProps {
  match: TournamentMatchDto;
  currentUserId?: string;
}

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'Completed':
      return 'green';
    case 'InProgress':
      return 'blue';
    case 'Pending':
      return 'default';
    case 'Bye':
      return 'gold';
    case 'Forfeit':
      return 'orange';
    default:
      return 'default';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'Completed':
      return <CheckCircleOutlined />;
    case 'InProgress':
      return <ClockCircleOutlined />;
    case 'Bye':
      return <TrophyOutlined />;
    default:
      return null;
  }
};

const getRoundTypeLabel = (roundType: string, t: any): string => {
  const map: Record<string, string> = {
    'Final': t('tournament.roundType.final', 'Finale'),
    'Finals': t('tournament.roundType.final', 'Finale'),
    'SemiFinal': t('tournament.roundType.semiFinal', 'Polufinale'),
    'SemiFinals': t('tournament.roundType.semiFinal', 'Polufinale'),
    'QuarterFinal': t('tournament.roundType.quarterFinal', 'Četvrtfinale'),
    'QuarterFinals': t('tournament.roundType.quarterFinal', 'Četvrtfinale'),
    'RoundOf16': t('tournament.roundType.roundOf16', 'Osmina finala'),
    'RoundOf32': t('tournament.roundType.roundOf32', 'Šesnaestina finala'),
    'RoundOf64': t('tournament.roundType.roundOf64', 'Krug od 64'),
  };
  return map[roundType] ?? roundType;
};

const MatchCard: React.FC<MatchCardProps> = ({ match, currentUserId }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  
  const isCurrentUserMatch = 
    match.participant1?.id === currentUserId || 
    match.participant2?.id === currentUserId;
  
  const isWinner = (participantId?: string) => {
    return participantId && match.winner?.id === participantId;
  };

  const isLoser = (participantId?: string) => {
    return participantId && match.loser?.id === participantId;
  };

  const isCurrentUser = (participantId?: string) => {
    return participantId === currentUserId;
  };

  const getStatusLabel = (status: string): string => {
    const statusMap: { [key: string]: string } = {
      'Completed': t('tournament.status.completed', 'Completed'),
      'InProgress': t('tournament.status.inProgress', 'In Progress'),
      'Pending': t('tournament.status.pending', 'Pending'),
      'Bye': t('tournament.status.bye', 'Bye'),
      'Forfeit': t('tournament.status.forfeit', 'Forfeit')
    };
    return statusMap[status] || status;
  };

  const ParticipantRow = ({ participant, score }: { participant?: TournamentParticipant | null; score?: number | null }) => {
    const isBye = match.status === 'Bye' && !participant;
    const name = participant?.displayName || (isBye ? t('tournament.bye', 'BYE') : t('tournament.tbd', 'TBD'));
    const won = isWinner(participant?.id);
    const lost = isLoser(participant?.id);
    const isMe = isCurrentUser(participant?.id);

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '4px 8px',
          backgroundColor: won ? token.colorSuccessBg : lost ? token.colorErrorBg : isMe ? token.colorInfoBg : 'transparent',
          borderRadius: 4,
          borderLeft: won ? `3px solid ${token.colorSuccess}` : lost ? `3px solid ${token.colorError}` : isMe ? `3px solid ${token.colorInfo}` : '3px solid transparent',
        }}
      >
        <Space size="small">
          <Avatar size="small" icon={<UserOutlined />} style={isMe ? { backgroundColor: token.colorInfo } : undefined} />
          <Text 
            strong={won || isMe}
            delete={lost ? true : undefined}
            type={isBye ? 'secondary' : undefined}
          >
            {name}
          </Text>
        </Space>
        {score !== undefined && score !== null && (
          <Tag color={won ? 'green' : lost ? 'red' : 'default'}>{score}</Tag>
        )}
      </div>
    );
  };

  return (
    <Card
      size="small"
      style={{
        marginBottom: 8,
        border: isCurrentUserMatch ? `2px solid ${token.colorInfo}` : `1px solid ${token.colorBorderSecondary}`,
        minWidth: 180,
      }}
      bodyStyle={{ padding: 8 }}
    >
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: 10 }}>{t('tournament.match', 'Match')} {match.bracketPosition}</Text>
          <Tag color={getStatusColor(match.status)} icon={getStatusIcon(match.status)} style={{ fontSize: 10 }}>
            {getStatusLabel(match.status)}
          </Tag>
        </div>
        <ParticipantRow 
          participant={match.participant1} 
          score={match.participant1Score} 
        />
        <div style={{ borderTop: `1px dashed ${token.colorBorderSecondary}`, margin: '2px 0' }} />
        <ParticipantRow 
          participant={match.participant2} 
          score={match.participant2Score} 
        />
      </Space>
    </Card>
  );
};

interface RoundColumnProps {
  round: TournamentRoundDto;
  currentUserId?: string;
  isLast: boolean;
}

const RoundColumn: React.FC<RoundColumnProps> = ({ round, currentUserId, isLast }) => {
  const { t } = useTranslation();

  return (
    <div style={{ minWidth: 200, padding: '0 8px' }}>
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <Tag color={isLast ? 'gold' : 'blue'}>{getRoundTypeLabel(round.roundType, t)}</Tag>
        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
          {t('tournament.matchCount', '{{count}} match', { count: round.matchCount })}
        </Text>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {round.matches.map((match) => (
          <MatchCard 
            key={match.id} 
            match={match} 
            currentUserId={currentUserId} 
          />
        ))}
      </div>
    </div>
  );
};

const TournamentBracketView: React.FC<TournamentBracketViewProps> = ({ 
  bracket, 
  currentUserId,
  isLoading 
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">{t('tournament.loadingBracket') || 'Loading bracket...'}</Text>
        </div>
      </div>
    );
  }

  if (!bracket || !bracket.rounds || bracket.rounds.length === 0) {
    return (
      <Empty 
        description={t('tournament.noBracketData') || 'No bracket data available'}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <div style={{ padding: 16 }}>
      {/* Tournament Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={4}>
          <TrophyOutlined style={{ marginRight: 8, color: '#faad14' }} />
          {bracket.title}
        </Title>
        <Space>
          <Tag>{t('tournament.participantCount', '{{count}} participants', { count: bracket.participantCount })}</Tag>
          <Tag>{t('tournament.bracketSize', 'Bracket: {{size}}', { size: bracket.bracketSize })}</Tag>
          {bracket.byeCount > 0 && (
            <Tooltip title={t('tournament.byeTooltip', '{{count}} participants auto-advance in Round 1', { count: bracket.byeCount })}>
              <Tag color="gold">{t('tournament.byeCount', '{{count}} BYE', { count: bracket.byeCount })}</Tag>
            </Tooltip>
          )}
        </Space>
      </div>

      {/* Bracket View */}
      <div style={{ overflowX: 'auto', paddingBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 24 }}>
          {bracket.rounds.map((round, index) => (
            <RoundColumn
              key={round.id}
              round={round}
              currentUserId={currentUserId}
              isLast={index === bracket.rounds.length - 1}
            />
          ))}
        </div>
      </div>

      {/* Third Place Match */}
      {bracket.thirdPlaceRound && (
        <div style={{ marginTop: 24, borderTop: `1px solid ${token.colorBorderSecondary}`, paddingTop: 16 }}>
          <Title level={5}>
            <TrophyOutlined style={{ marginRight: 8, color: '#cd7f32' }} />
            {t('tournament.thirdPlaceMatch') || 'Third Place Match'}
          </Title>
          <div style={{ maxWidth: 220 }}>
            {bracket.thirdPlaceRound.matches.map((match) => (
              <MatchCard 
                key={match.id} 
                match={match} 
                currentUserId={currentUserId} 
              />
            ))}
          </div>
        </div>
      )}

      {/* Placements */}
      {bracket.placements && (bracket.placements.first || bracket.placements.second || bracket.placements.third) && (
        <div style={{ marginTop: 24, borderTop: `1px solid ${token.colorBorderSecondary}`, paddingTop: 16 }}>
          <Row gutter={16}>
            {bracket.placements.first && (
              <Col>
                <Card size="small" style={{ backgroundColor: token.colorWarningBg, borderColor: token.colorWarningBorder }}>
                  <Space>
                    <span style={{ fontSize: 24 }}>🥇</span>
                    <div>
                      <Text strong>{t('tournament.firstPlace')}</Text>
                      <br />
                      <Text>{bracket.placements.first.displayName}</Text>
                    </div>
                  </Space>
                </Card>
              </Col>
            )}
            {bracket.placements.second && (
              <Col>
                <Card size="small" style={{ backgroundColor: token.colorFillSecondary, borderColor: token.colorBorderSecondary }}>
                  <Space>
                    <span style={{ fontSize: 24 }}>🥈</span>
                    <div>
                      <Text strong>{t('tournament.secondPlace')}</Text>
                      <br />
                      <Text>{bracket.placements.second.displayName}</Text>
                    </div>
                  </Space>
                </Card>
              </Col>
            )}
            {bracket.placements.third && (
              <Col>
                <Card size="small" style={{ backgroundColor: token.colorFillAlter, borderColor: token.colorBorder }}>
                  <Space>
                    <span style={{ fontSize: 24 }}>🥉</span>
                    <div>
                      <Text strong>{t('tournament.thirdPlace')}</Text>
                      <br />
                      <Text>{bracket.placements.third.displayName}</Text>
                    </div>
                  </Space>
                </Card>
              </Col>
            )}
          </Row>
        </div>
      )}
    </div>
  );
};

export default TournamentBracketView;
